<template>
  <a-modal
    :open="visible"
    title="QR코드 대량 등록"
    :width="900"
    :footer="null"
    @cancel="handleCancel"
  >
    <div class="modal-content">
      <!-- Step 1: 파일 업로드 -->
      <div v-if="step === 1" class="upload-section">
        <div class="upload-header">
          <h4>엑셀 파일 업로드</h4>
          <a-button type="link" @click="downloadTemplate">
            <template #icon><DownloadOutlined /></template>
            템플릿 다운로드
          </a-button>
        </div>

        <a-upload-dragger
          :before-upload="handleFileSelect"
          :show-upload-list="false"
          accept=".xlsx,.xls"
        >
          <p class="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p class="ant-upload-text">클릭하거나 파일을 드래그하여 업로드</p>
          <p class="ant-upload-hint">Excel 파일(.xlsx, .xls)만 지원됩니다. 최대 500건</p>
        </a-upload-dragger>

        <div v-if="selectedFile" class="selected-file">
          <FileExcelOutlined />
          <span class="file-name">{{ selectedFile.name }}</span>
          <a-button type="link" danger size="small" @click="clearFile">삭제</a-button>
        </div>

        <div class="template-info">
          <div class="section-header">
            <h5>엑셀 템플릿 형식</h5>
            <span class="required-hint"><span class="required-mark">*</span> 필수 항목</span>
          </div>
          <a-table
            :columns="templateColumns"
            :data-source="templateExample"
            :pagination="false"
            size="small"
            bordered
            class="template-table"
          />
          <div class="template-hints">
            <div class="hint-item">
              <i class="fa-solid fa-circle-info info-icon"></i>
              <span><strong>상태</strong>: active(활성) 또는 inactive(비활성). 기본값: active</span>
            </div>
            <div class="hint-item warning">
              <i class="fa-solid fa-triangle-exclamation warning-icon"></i>
              <span><strong>통계수집</strong>: Y(수집) 또는 N(미수집). <span class="warning-text">미수집 시 URL 변경 불가</span></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 2: 검증 결과 및 미리보기 -->
      <div v-if="step === 2" class="preview-section">
        <div class="validation-summary">
          <a-alert
            v-if="validationResult.errors?.length === 0"
            type="success"
            message="검증 완료"
            :description="`총 ${parsedData.length}건의 데이터가 유효합니다.`"
            show-icon
          />
          <a-alert
            v-else
            type="warning"
            message="일부 데이터에 오류가 있습니다"
            :description="`총 ${parsedData.length}건 중 ${validationResult.errors?.length || 0}건에 오류가 있습니다.`"
            show-icon
          />
        </div>

        <!-- 옵션 설정 -->
        <div class="options-section">
          <h5>생성 옵션</h5>
          <div class="options-list">
            <a-checkbox v-model:checked="options.skipErrors">
              오류가 있는 항목은 건너뛰고 계속 진행
            </a-checkbox>
            <a-checkbox v-model:checked="options.createMissingCategory">
              존재하지 않는 카테고리 자동 생성
            </a-checkbox>
            <a-checkbox v-model:checked="options.createMissingTags">
              존재하지 않는 태그 자동 생성
            </a-checkbox>
          </div>
        </div>

        <!-- 오류 목록 -->
        <div v-if="validationResult.errors?.length > 0" class="errors-section">
          <h5>오류 목록</h5>
          <a-table
            :columns="errorColumns"
            :data-source="validationResult.errors"
            :pagination="{ pageSize: 5 }"
            size="small"
            :row-key="(record, index) => index"
          >
            <template #value="{ record }">
              <span class="error-value">{{ record.value || '-' }}</span>
            </template>
          </a-table>
        </div>

        <!-- 데이터 미리보기 -->
        <div class="preview-data">
          <h5>데이터 미리보기 (처음 10건)</h5>
          <a-table
            :columns="previewColumns"
            :data-source="previewData"
            :pagination="false"
            size="small"
            :row-key="(record, index) => index"
            :scroll="{ x: 800 }"
          >
            <template #status="{ record }">
              <a-tag :color="getRowStatus(record.row).color">
                {{ getRowStatus(record.row).text }}
              </a-tag>
            </template>
            <template #tracking="{ record }">
              <a-tag :color="getTrackingStatus(record.tracking).color">
                {{ getTrackingStatus(record.tracking).text }}
              </a-tag>
            </template>
          </a-table>
        </div>
      </div>

      <!-- Step 3: 결과 -->
      <div v-if="step === 3" class="result-section">
        <a-result
          :status="resultStatus"
          :title="resultTitle"
          :sub-title="resultSubTitle"
        >
          <template #extra>
            <a-button type="primary" class="yellow-button" @click="handleClose">
              확인
            </a-button>
          </template>
        </a-result>

        <div v-if="createResult.errors?.length > 0" class="result-errors">
          <h5>생성 실패 항목</h5>
          <a-table
            :columns="errorColumns"
            :data-source="createResult.errors"
            :pagination="{ pageSize: 5 }"
            size="small"
            :row-key="(record, index) => index"
          />
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer" v-if="step !== 3">
        <a-button @click="handleCancel">취소</a-button>
        <a-button v-if="step === 2" @click="step = 1">이전</a-button>
        <a-button
          v-if="step === 1"
          type="primary"
          class="yellow-button"
          :loading="validating"
          :disabled="!selectedFile"
          @click="handleValidate"
        >
          검증
        </a-button>
        <a-button
          v-if="step === 2"
          type="primary"
          class="yellow-button"
          :loading="creating"
          :disabled="!canCreate"
          @click="handleCreate"
        >
          생성 ({{ validCount }}건)
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue';
import { InboxOutlined, DownloadOutlined, FileExcelOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import * as XLSX from 'xlsx';
import QrService from '../service/QrService';
import { toBulkQrItems } from '../utils/qrBulkExcel';

export default defineComponent({
  name: 'QrBulkUploadModal',
  components: {
    InboxOutlined,
    DownloadOutlined,
    FileExcelOutlined
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    categories: {
      type: Array,
      default: () => []
    },
    tags: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:visible', 'success'],
  setup(props, { emit }) {
    const step = ref(1);
    const selectedFile = ref(null);
    const parsedData = ref([]);
    const validationResult = ref({ errors: [] });
    const createResult = ref({ success: 0, failed: 0, errors: [] });
    const validating = ref(false);
    const creating = ref(false);

    const options = ref({
      skipErrors: true,
      createMissingCategory: false,
      createMissingTags: false
    });

    const templateColumns = [
      { title: 'QR코드명 *', dataIndex: 'name', width: 130 },
      { title: '목적지 URL *', dataIndex: 'targetUrl', width: 180 },
      { title: '설명', dataIndex: 'description', width: 130 },
      { title: '카테고리', dataIndex: 'category', width: 80 },
      { title: '태그', dataIndex: 'tags', width: 100 },
      { title: '상태', dataIndex: 'status', width: 70 },
      { title: '통계수집', dataIndex: 'tracking', width: 70 }
    ];

    const templateExample = [
      {
        name: '마케팅 QR 1',
        targetUrl: 'https://example.com/landing1',
        description: '2024년 캠페인용',
        category: '마케팅',
        tags: '프로모션, 이벤트',
        status: 'active',
        tracking: 'Y'
      }
    ];

    const errorColumns = [
      { title: '행', dataIndex: 'row', width: 60 },
      { title: '필드', dataIndex: 'field', width: 100 },
      { title: '오류 내용', dataIndex: 'message', width: 250 },
      { title: '값', key: 'value', slots: { customRender: 'value' }, width: 150 }
    ];

    const previewColumns = [
      { title: '행', dataIndex: 'row', width: 50 },
      { title: '상태', key: 'status', slots: { customRender: 'status' }, width: 80 },
      { title: 'QR코드명', dataIndex: 'name', width: 130 },
      { title: '목적지 URL', dataIndex: 'targetUrl', width: 180, ellipsis: true },
      { title: '카테고리', dataIndex: 'category', width: 80 },
      { title: '태그', dataIndex: 'tags', width: 100 },
      { title: '통계수집', key: 'tracking', slots: { customRender: 'tracking' }, width: 80 }
    ];

    const previewData = computed(() => {
      return parsedData.value.slice(0, 10).map((item, index) => ({
        ...item,
        row: index + 2
      }));
    });

    const validCount = computed(() => {
      if (!options.value.skipErrors) {
        return validationResult.value.errors?.length === 0 ? parsedData.value.length : 0;
      }
      const errorRows = new Set((validationResult.value.errors || []).map(e => e.row));
      return parsedData.value.length - errorRows.size;
    });

    const canCreate = computed(() => {
      return validCount.value > 0;
    });

    const resultStatus = computed(() => {
      if (createResult.value.success > 0 && createResult.value.failed === 0) return 'success';
      if (createResult.value.success > 0 && createResult.value.failed > 0) return 'warning';
      return 'error';
    });

    const resultTitle = computed(() => {
      if (createResult.value.success > 0 && createResult.value.failed === 0) {
        return `${createResult.value.success}개 QR코드 생성 완료`;
      }
      if (createResult.value.success > 0 && createResult.value.failed > 0) {
        return `${createResult.value.success}개 성공, ${createResult.value.failed}개 실패`;
      }
      return 'QR코드 생성 실패';
    });

    const resultSubTitle = computed(() => {
      if (createResult.value.failed > 0) {
        return '실패한 항목은 아래 목록을 확인해주세요.';
      }
      return '모든 QR코드가 성공적으로 생성되었습니다.';
    });

    const getRowStatus = (rowNum) => {
      const hasError = (validationResult.value.errors || []).some(e => e.row === rowNum);
      if (hasError) {
        return { color: 'red', text: '오류' };
      }
      return { color: 'green', text: '정상' };
    };

    const getTrackingStatus = (tracking) => {
      const value = String(tracking || 'Y').toLowerCase();
      const isTracking = !(value === 'n' || value === 'no' || value === 'false' || value === '0');
      return isTracking
        ? { color: 'blue', text: '수집' }
        : { color: 'orange', text: '미수집' };
    };

    const downloadTemplate = () => {
      const templateData = [
        ['QR코드명', '목적지 URL', '설명', '카테고리', '태그', '상태', '통계수집'],
        ['마케팅 QR 1', 'https://example.com/landing1', '2024년 캠페인용', '마케팅', '프로모션, 이벤트', 'active', 'Y'],
        ['제품 QR A', 'https://example.com/product/a', '제품 A 상세페이지', '제품', '신제품', 'active', 'Y'],
        ['직접 URL QR', 'https://example.com/direct', '통계 미수집 QR (URL 변경 불가)', '', '', 'active', 'N']
      ];

      const ws = XLSX.utils.aoa_to_sheet(templateData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'QR코드 템플릿');

      ws['!cols'] = [
        { wch: 20 },
        { wch: 40 },
        { wch: 35 },
        { wch: 15 },
        { wch: 20 },
        { wch: 10 },
        { wch: 10 }
      ];

      XLSX.writeFile(wb, 'QR코드_대량등록_템플릿.xlsx');
    };

    const handleFileSelect = (file) => {
      if (!file.name.match(/\.(xlsx|xls)$/i)) {
        message.warning('Excel 파일(.xlsx, .xls)만 업로드 가능합니다.');
        return false;
      }

      selectedFile.value = file;
      parseExcelFile(file);
      return false;
    };

    const parseExcelFile = (file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

          if (jsonData.length < 2) {
            message.warning('데이터가 없습니다. 헤더 행과 최소 1개의 데이터 행이 필요합니다.');
            clearFile();
            return;
          }

          const items = toBulkQrItems(jsonData);

          if (items.length > 500) {
            message.warning('최대 500건까지만 등록 가능합니다.');
            clearFile();
            return;
          }

          parsedData.value = items;

          message.success(`${parsedData.value.length}건의 데이터를 읽었습니다.`);
        } catch (err) {
          console.error('Excel 파싱 오류:', err);
          message.error('Excel 파일을 읽는 중 오류가 발생했습니다.');
          clearFile();
        }
      };
      reader.readAsArrayBuffer(file);
    };

    const clearFile = () => {
      selectedFile.value = null;
      parsedData.value = [];
      validationResult.value = { errors: [] };
    };

    const handleValidate = async () => {
      if (parsedData.value.length === 0) {
        message.warning('업로드된 데이터가 없습니다.');
        return;
      }

      validating.value = true;
      try {
        // URL 인코딩은 QrService에서 처리됨
        const result = await QrService.bulkValidate({
          items: parsedData.value,
          options: options.value
        });
        // 검증 요청 자체가 실패하면 결과가 없다. 전 행 '정상'으로 넘어가지 않고 업로드 단계에 머문다.
        if (!result) return;
        validationResult.value = result;
        step.value = 2;
      } finally {
        validating.value = false;
      }
    };

    const handleCreate = async () => {
      creating.value = true;
      try {
        // URL 인코딩은 QrService에서 처리됨
        const result = await QrService.bulkCreate({
          items: parsedData.value,
          options: options.value
        });
        createResult.value = result;
        step.value = 3;

        if (result.success > 0) {
          emit('success');
        }
      } finally {
        creating.value = false;
      }
    };

    const handleCancel = () => {
      emit('update:visible', false);
    };

    const handleClose = () => {
      emit('update:visible', false);
    };

    watch(() => props.visible, (newVal) => {
      if (newVal) {
        step.value = 1;
        selectedFile.value = null;
        parsedData.value = [];
        validationResult.value = { errors: [] };
        createResult.value = { success: 0, failed: 0, errors: [] };
        options.value = {
          skipErrors: true,
          createMissingCategory: false,
          createMissingTags: false
        };
      }
    });

    return {
      step,
      selectedFile,
      parsedData,
      validationResult,
      createResult,
      validating,
      creating,
      options,
      templateColumns,
      templateExample,
      errorColumns,
      previewColumns,
      previewData,
      validCount,
      canCreate,
      resultStatus,
      resultTitle,
      resultSubTitle,
      getRowStatus,
      getTrackingStatus,
      downloadTemplate,
      handleFileSelect,
      clearFile,
      handleValidate,
      handleCreate,
      handleCancel,
      handleClose
    };
  }
});
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';

.modal-content {
  padding: $spacing-sm 0;
}

.upload-section {
  .upload-header {
    @include flex-between;
    margin-bottom: $spacing-md;

    h4 {
      margin: 0;
      font-size: 16px;
      font-weight: $font-weight-bold;
      color: $text-dark;
    }
  }

  .selected-file {
    display: flex;
    align-items: center;
    gap: $spacing-sm;
    padding: 10px $spacing-md;
    background: linear-gradient(135deg, #f6ffed 0%, #e6f7e6 100%);
    border: 1px solid #b7eb8f;
    border-radius: $border-radius-md;
    margin-top: $spacing-md;

    .anticon {
      color: $success-color;
      font-size: 22px;
    }

    .file-name {
      flex: 1;
      font-weight: $font-weight-medium;
      color: $text-dark;
    }
  }

  .template-info {
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid $border-color;

    .section-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;

      h5 {
        margin: 0;
        font-size: 15px;
        font-weight: $font-weight-bold;
        color: $text-dark;
      }

      .required-hint {
        font-size: $font-size-xs;
        color: $text-muted;

        .required-mark {
          color: $danger-color;
          margin-right: 2px;
        }
      }
    }

    .template-table {
      margin-bottom: 12px;
    }

    .template-hints {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .hint-item {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        padding: 8px 12px;
        background-color: #f5f5f5;
        border-radius: $border-radius-sm;
        font-size: $font-size-sm;
        color: $text-muted;
        line-height: 1.5;

        .info-icon {
          color: #1890ff;
          margin-top: 2px;
          flex-shrink: 0;
        }

        .warning-icon {
          color: #fa8c16;
          margin-top: 2px;
          flex-shrink: 0;
        }

        &.warning {
          background-color: #fff7e6;
          border: 1px solid #ffd591;
        }

        .warning-text {
          color: #fa541c;
          font-weight: $font-weight-bold;
        }

        strong {
          color: $text-dark;
        }
      }
    }
  }
}

.preview-section {
  .validation-summary {
    margin-bottom: 16px;
  }

  .options-section {
    padding: 16px;
    background: $bg-light;
    border-radius: $border-radius-md;
    margin-bottom: 16px;
    border: 1px solid $border-color;

    h5 {
      margin: 0 0 12px;
      font-size: 14px;
      font-weight: $font-weight-bold;
      color: $text-dark;
    }

    .options-list {
      display: flex;
      flex-direction: column;
      gap: 10px;

      .ant-checkbox-wrapper {
        margin: 0;
        line-height: 1.5;

        &:hover {
          color: $primary-color;
        }
      }
    }
  }

  .errors-section {
    margin-bottom: 16px;

    h5 {
      margin-bottom: 12px;
      font-size: 14px;
      font-weight: $font-weight-bold;
      color: $danger-color;
      display: flex;
      align-items: center;
      gap: 6px;

      &::before {
        content: '⚠';
        font-size: 14px;
      }
    }

    .error-value {
      color: $text-muted;
      font-size: $font-size-sm;
      max-width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .preview-data {
    h5 {
      margin-bottom: 12px;
      font-size: 14px;
      font-weight: $font-weight-bold;
      color: $text-dark;
    }
  }
}

.result-section {
  .result-errors {
    margin-top: 24px;
    padding: 16px;
    background: $bg-light;
    border-radius: $border-radius-md;
    border: 1px solid $border-color;

    h5 {
      margin-bottom: 12px;
      font-size: 14px;
      font-weight: $font-weight-bold;
      color: $danger-color;
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: $spacing-sm;
  padding-top: 16px;
  border-top: 1px solid $border-color;
  margin-top: 20px;
}

.yellow-button {
  background-color: $yellow-button-color;
  border-color: $yellow-button-color;
  color: $text-dark;

  &:hover {
    background-color: $yellow-button-color-hover;
    border-color: $yellow-button-color-hover;
  }
}
</style>
